(function () {
  window.money = (cents) => {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  };

  const BRAND_LABELS = {
    meta: 'Meta / Ray-Ban',
    oakley: 'Oakley Meta',
    xreal: 'XREAL',
    rayneo: 'RayNeo',
    rokid: 'Rokid',
    viture: 'VITURE'
  };

  const MODEL_LABELS = {
    'wayfarer-large': 'Wayfarer Large',
    wayfarer: 'Wayfarer',
    round: 'Round',
    headliner: 'Headliner',
    skyler: 'Skyler',
    hstn: 'HSTN',
    sphaera: 'Sphaera',
    one: 'One',
    'one-pro': 'One Pro',
    x2: 'X2',
    'rokid-glasses': 'Rokid Glasses',
    'rokid-ai-glasses': 'Rokid AI Glasses',
    max: 'Max',
    'max-pro': 'Max Pro',
    'one-lite': 'One Lite',
    'one-xr': 'One XR',
    pro: 'Pro'
  };

  const BRAND_COLLECTION_HANDLES = {
    meta: 'meta-ray-ban',
    oakley: 'oakley',
    xreal: 'xreal',
    rayneo: 'rayneo',
    rokid: 'rokid',
    viture: 'viture'
  };

  function normalizeKey(value) {
    return (value || '').trim().toLowerCase();
  }

  function readCsv(value) {
    return (value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  /** Click target can be a Text node in some browsers — closest() needs an Element. */
  function eventTargetElement(event) {
    const raw = event.target;
    if (raw instanceof Element) return raw;
    if (raw && raw.nodeType === Node.TEXT_NODE && raw.parentElement) return raw.parentElement;
    return null;
  }

  function getFitParams() {
    const params = new URLSearchParams(window.location.search);

    return {
      brand: normalizeKey(params.get('brand')),
      model: normalizeKey(params.get('model'))
    };
  }

  function normalizeFitSelection(selection) {
    const brand = normalizeKey(selection?.brand);
    const model = normalizeKey(selection?.model);

    if (!brand || !model) return null;

    return {
      brand,
      model,
      brandLabel: (selection?.brandLabel || BRAND_LABELS[brand] || brand).trim(),
      modelLabel: (selection?.modelLabel || MODEL_LABELS[model] || model).trim()
    };
  }

  function combineFitLabels(brandLabel, modelLabel) {
    const brand = String(brandLabel || '').trim();
    const model = String(modelLabel || '').trim();
    if (!brand || !model) return brand || model;
    const brandNormalized = brand.toLowerCase();
    const modelNormalized = model.toLowerCase();
    return modelNormalized === brandNormalized || modelNormalized.startsWith(`${brandNormalized} `)
      ? model
      : `${brand} ${model}`;
  }

  function getSelectionLabel(selection) {
    const normalized = normalizeFitSelection(selection);
    if (!normalized) return '';
    return combineFitLabels(normalized.brandLabel, normalized.modelLabel);
  }

  const FIT_STORAGE_KEY = 'sgg.fit.v1';

  function getStoredFitSelection() {
    try {
      const raw = window.localStorage.getItem(FIT_STORAGE_KEY);
      if (!raw) return null;
      return normalizeFitSelection(JSON.parse(raw));
    } catch (error) {
      return null;
    }
  }

  function setStoredFitSelection(selection) {
    const normalized = normalizeFitSelection(selection);
    if (!normalized) return null;

    try {
      window.localStorage.setItem(FIT_STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      // Ignore storage failures (Safari private mode, full quota, etc.)
    }

    return normalized;
  }

  function clearStoredFitSelection() {
    try {
      window.localStorage.removeItem(FIT_STORAGE_KEY);
    } catch (error) {
      return;
    }
  }

  function buildFitCollectionUrl(selection) {
    const normalized = normalizeFitSelection(selection);

    if (!normalized) return '/collections';

    const handle = BRAND_COLLECTION_HANDLES[normalized.brand];

    if (!handle) return '/collections';

    return `/collections/${handle}/model-${normalized.model}`;
  }

  function getChooserBrandOption(brandSelect, brandKey) {
    if (!(brandSelect instanceof HTMLSelectElement) || !brandKey) return null;

    return Array.from(brandSelect.options).find((option) => option.value === brandKey) || null;
  }

  function getChooserModelOptions(brandOption) {
    if (!(brandOption instanceof HTMLOptionElement)) return [];

    return (brandOption.dataset.models || '')
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const [key, label] = item.split(':');

        return {
          key: normalizeKey(key),
          label: (label || key || '').trim()
        };
      })
      .filter((item) => item.key && item.label);
  }

  function populateChooserModelSelect(modelSelect, brandOption, selectedModel) {
    if (!(modelSelect instanceof HTMLSelectElement)) return;

    const options = getChooserModelOptions(brandOption);
    modelSelect.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = options.length ? 'Select model' : 'Choose brand first';
    modelSelect.appendChild(placeholder);

    options.forEach((model) => {
      const option = document.createElement('option');
      option.value = model.key;
      option.textContent = model.label;
      option.dataset.modelLabel = model.label;
      modelSelect.appendChild(option);
    });

    modelSelect.disabled = options.length === 0;

    if (selectedModel) {
      const matching = Array.from(modelSelect.options).find((option) => option.value === selectedModel);

      if (matching) {
        modelSelect.value = selectedModel;
      }
    }
  }

  function getChooserSelection(chooser) {
    if (!(chooser instanceof HTMLElement)) return null;

    const brandSelect = chooser.querySelector('[data-sgg-fit-brand]');
    const modelSelect = chooser.querySelector('[data-sgg-fit-model]');

    if (!(brandSelect instanceof HTMLSelectElement) || !(modelSelect instanceof HTMLSelectElement)) {
      return null;
    }

    const brandOption = brandSelect.selectedOptions[0];
    const modelOption = modelSelect.selectedOptions[0];

    return normalizeFitSelection({
      brand: brandSelect.value,
      model: modelSelect.value,
      brandLabel: brandOption?.dataset.brandLabel || brandOption?.textContent || '',
      modelLabel: modelOption?.dataset.modelLabel || modelOption?.textContent || ''
    });
  }

  function updateChooserSubmit(chooser, selection) {
    if (!(chooser instanceof HTMLElement)) return;

    const submit = chooser.querySelector('[data-sgg-fit-submit]');

    if (!(submit instanceof HTMLButtonElement)) return;

    const normalized = normalizeFitSelection(selection);

    if (!normalized) {
      submit.disabled = true;
      submit.textContent = 'Show matching gear';
      return;
    }

    submit.disabled = false;
    submit.textContent = `Shop ${normalized.modelLabel}`;
  }

  function ensureHiddenInput(form, name, value) {
    form.querySelectorAll(`input[data-sgg-fit-param="${name}"]`).forEach((input) => input.remove());

    if (!value) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute('data-sgg-fit-param', name);
    form.appendChild(input);
  }

  function syncFitParamsToForm(form) {
    if (!(form instanceof HTMLFormElement)) return;

    const { brand, model } = getFitParams();
    ensureHiddenInput(form, 'brand', brand);
    ensureHiddenInput(form, 'model', model);
  }

  function preserveFitParamsOnLink(link) {
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!link.href || link.getAttribute('href')?.startsWith('#')) return;

    const { brand, model } = getFitParams();
    if (!brand && !model) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;

    if (brand && !url.searchParams.has('brand')) {
      url.searchParams.set('brand', brand);
    }

    if (model && !url.searchParams.has('model')) {
      url.searchParams.set('model', model);
    }

    link.href = `${url.pathname}${url.search}${url.hash}`;
  }

  function syncFitFilterLinks(selectionOverride) {
    const params = getFitParams();
    const activeSelection = normalizeFitSelection(selectionOverride);
    const activeBrand = params.brand || activeSelection?.brand || '';
    const activeModel = params.model || activeSelection?.model || '';

    document.querySelectorAll('[data-sgg-filter-link]').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;

      const linkBrand = link.dataset.brandKey || '';
      const linkModel = link.dataset.modelKey || '';
      const isBrandLink = Boolean(linkBrand) && !linkModel;
      const isModelLink = Boolean(linkBrand) && Boolean(linkModel);
      const isActive =
        (isBrandLink && activeBrand === linkBrand && !activeModel) ||
        (isModelLink && activeBrand === linkBrand && activeModel === linkModel);

      link.classList.toggle('is-active', isActive);

      if (isActive && link instanceof HTMLAnchorElement) {
        const linkUrl = new URL(link.href, window.location.origin);

        if (linkUrl.pathname === window.location.pathname) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      } else if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    document.querySelectorAll('[data-sgg-model-nav-group]').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;

      const trigger = group.querySelector('[data-sgg-model-trigger]');
      const matchingModel = group.querySelector('[data-sgg-filter-link][data-model-key].is-active');
      const matchingBrand = group.querySelector('[data-sgg-filter-link][data-brand-key]:not([data-model-key]).is-active');
      const shouldHighlight = Boolean(matchingModel || matchingBrand);

      if (trigger instanceof HTMLElement) {
        trigger.classList.toggle('is-active', shouldHighlight);
      }
    });
  }

  function buildFitLabel(params) {
    const brandLabel = BRAND_LABELS[params.brand] || '';
    const modelLabel = MODEL_LABELS[params.model] || '';

    return combineFitLabels(brandLabel, modelLabel);
  }

  function makeFilterLink(url, text) {
    const link = document.createElement('a');
    link.className = 'sgg-active-filter';
    link.href = url;
    link.textContent = text;
    return link;
  }

  function renderFitFilterSummary(container, params) {
    if (!(container instanceof HTMLElement)) return;

    const { brand, model } = params;
    container.innerHTML = '';

    if (!brand && !model) {
      container.hidden = true;
      return;
    }

    const currentUrl = new URL(window.location.href);

    if (brand) {
      const brandUrl = new URL(currentUrl.toString());
      brandUrl.searchParams.delete('brand');
      brandUrl.searchParams.delete('model');

      const brandLink = makeFilterLink(
        `${brandUrl.pathname}${brandUrl.search}${brandUrl.hash}`,
        `Brand: ${BRAND_LABELS[brand] || brand}`
      );
      brandLink.appendChild(document.createElement('span')).textContent = 'x';
      container.appendChild(brandLink);
    }

    if (model) {
      const modelUrl = new URL(currentUrl.toString());
      modelUrl.searchParams.delete('model');

      const modelLink = makeFilterLink(
        `${modelUrl.pathname}${modelUrl.search}${modelUrl.hash}`,
        `Model: ${MODEL_LABELS[model] || model}`
      );
      modelLink.appendChild(document.createElement('span')).textContent = 'x';
      container.appendChild(modelLink);
    }

    const clearLink = document.createElement('a');
    clearLink.className = 'sgg-active-filter sgg-active-filter--clear';
    const clearUrl = new URL(currentUrl.toString());
    clearUrl.searchParams.delete('brand');
    clearUrl.searchParams.delete('model');
    clearLink.href = `${clearUrl.pathname}${clearUrl.search}${clearUrl.hash}`;
    clearLink.textContent = 'Clear fit filter';
    container.appendChild(clearLink);

    container.hidden = false;
  }

  function updateResultsLabel(node, visibleCount, params) {
    if (!(node instanceof HTMLElement)) return;

    const label = buildFitLabel(params);
    const productCopy = `${visibleCount} product${visibleCount === 1 ? '' : 's'}`;
    node.textContent = label ? `${productCopy} for ${label}` : productCopy;
  }

  function getCollectionContextSelection(root) {
    if (!(root instanceof HTMLElement)) return null;

    return normalizeFitSelection({
      brand: root.dataset.sggCurrentBrand,
      model: root.dataset.sggCurrentModel,
      brandLabel: root.dataset.sggCurrentBrandLabel,
      modelLabel: root.dataset.sggCurrentModelLabel
    });
  }

  function getFitStateForProduct({ brands, models, universal }, selection) {
    const normalizedSelection = normalizeFitSelection(selection);

    if (!normalizedSelection) {
      return { status: 'empty' };
    }

    const sameBrand = brands.includes(normalizedSelection.brand);
    const sameModel = models.includes(normalizedSelection.model);
    const isUniversalMatch = universal && (brands.length === 0 || sameBrand);

    if (sameBrand && sameModel) {
      return { status: 'fit' };
    }

    if (isUniversalMatch) {
      return { status: 'universal' };
    }

    return { status: 'warning' };
  }

  function renderProductFitChecks(selection) {
    const normalizedSelection = normalizeFitSelection(selection);

    document.querySelectorAll('[data-sgg-product-root]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;

      const panel = root.querySelector('[data-sgg-product-fit-check]');
      const titleNode = root.querySelector('[data-sgg-fit-check-title]');
      const copyNode = root.querySelector('[data-sgg-fit-check-copy]');
      const linkNode = root.querySelector('[data-sgg-fit-check-link]');
      const clearButton = root.querySelector('.sgg-product-fit-check-clear[data-sgg-fit-clear]');

      if (!(panel instanceof HTMLElement) || !(titleNode instanceof HTMLElement) || !(copyNode instanceof HTMLElement)) {
        return;
      }

      const compatibilityText = root.dataset.sggCompatibilityText || 'supported smart glasses models';

      panel.classList.remove('is-fit', 'is-warning', 'is-empty', 'is-universal');

      if (!normalizedSelection) {
        titleNode.textContent = 'Check fit before ordering';
        copyNode.textContent = `This product is designed for ${compatibilityText}. If you are unsure, compare by model before checkout.`;

        if (linkNode instanceof HTMLAnchorElement) {
          linkNode.href = '/collections';
          linkNode.textContent = 'Shop by model';
        }

        panel.classList.add('is-empty');
        return;
      }

      const fitState = getFitStateForProduct(
        {
          brands: readCsv(root.dataset.sggBrandKeys),
          models: readCsv(root.dataset.sggModelKeys),
          universal: root.dataset.sggUniversal === 'true'
        },
        normalizedSelection
      );

      const selectionLabel = getSelectionLabel(normalizedSelection);

      if (root.dataset.sggFitCheckMode === 'speaker-layout') {
        titleNode.textContent = 'Compare your speaker layout';
        copyNode.textContent = `Your selected glasses: ${selectionLabel}. CHANNEL supports Rokid, Ray-Ban Meta, and other compatible open-ear smart glasses. Compare your temple shape and speaker position with the gallery; the saved model does not confirm the attachment fit.`;
        panel.classList.add('is-universal');
      } else if (fitState.status === 'fit') {
        titleNode.textContent = `Fits your ${selectionLabel}`;
        copyNode.textContent = `This product is tagged for ${selectionLabel}, so the saved fit matches what is shown on this page.`;
        panel.classList.add('is-fit');
      } else if (fitState.status === 'universal') {
        titleNode.textContent = `Works with your ${selectionLabel}`;
        copyNode.textContent = `This item is marked as universal fit. Review the supported models listed on the page if you want a second check before ordering.`;
        panel.classList.add('is-universal');
      } else {
        titleNode.textContent = 'Check fit before ordering';
        copyNode.textContent = `Your saved model is ${selectionLabel}. This product is currently tagged for ${compatibilityText}, so confirm compatibility before you add it to cart.`;
        panel.classList.add('is-warning');
      }

      if (linkNode instanceof HTMLAnchorElement) {
        linkNode.href = buildFitCollectionUrl(normalizedSelection);
        linkNode.textContent = `Shop gear for ${normalizedSelection.modelLabel}`;
      }
    });
  }

  function refreshFitUi(selectionOverride) {
    const normalizedSelection = normalizeFitSelection(selectionOverride);
    syncFitFilterLinks(normalizedSelection);
    renderFitChoosers(normalizedSelection);
    renderProductFitChecks(normalizedSelection);
  }

  function renderFitChoosers(selectionOverride) {
    const params = getFitParams();
    const normalizedSelection =
      normalizeFitSelection(selectionOverride) ||
      normalizeFitSelection({
        brand: params.brand,
        model: params.model
      });

    document.querySelectorAll('[data-sgg-fit-chooser]').forEach((chooser) => {
      if (!(chooser instanceof HTMLElement)) return;

      const brandSelect = chooser.querySelector('[data-sgg-fit-brand]');
      const modelSelect = chooser.querySelector('[data-sgg-fit-model]');

      if (!(brandSelect instanceof HTMLSelectElement) || !(modelSelect instanceof HTMLSelectElement)) {
        return;
      }

      if (normalizedSelection?.brand) {
        brandSelect.value = normalizedSelection.brand;
      } else {
        brandSelect.value = '';
      }

      const brandOption =
        getChooserBrandOption(brandSelect, brandSelect.value) || brandSelect.selectedOptions[0];
      populateChooserModelSelect(modelSelect, brandOption, normalizedSelection?.model);
      updateChooserSubmit(chooser, getChooserSelection(chooser));
    });
  }

  function bindFitSelectionTriggers() {
    document.querySelectorAll('[data-sgg-fit-select]').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;

      link.addEventListener('click', () => {
        const selection = normalizeFitSelection({
          brand: link.dataset.brandKey,
          model: link.dataset.modelKey,
          brandLabel: link.dataset.brandLabel,
          modelLabel: link.dataset.modelLabel
        });

        if (!selection) return;

        setStoredFitSelection(selection);
        refreshFitUi(selection);
      });
    });

    document.querySelectorAll('[data-sgg-fit-clear]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        clearStoredFitSelection();
        refreshFitUi(null);
      });
    });
  }

  function initCollectionDisclosures() {
    const mobile = window.matchMedia('(max-width: 749px)');
    document.querySelectorAll('[data-sgg-collection-root] [data-sgg-mobile-disclosure]').forEach((disclosure) => {
      if (!(disclosure instanceof HTMLDetailsElement) || disclosure.dataset.sggDisclosureReady) return;
      disclosure.dataset.sggDisclosureReady = 'true';
      const syncViewport = () => { disclosure.open = !mobile.matches; };
      syncViewport();
      mobile.addEventListener('change', syncViewport);
    });
  }

  function initCollectionControls() {
    document.querySelectorAll('[data-sgg-collection-form]').forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;

      syncFitParamsToForm(form);

      form.addEventListener('submit', () => {
        syncFitParamsToForm(form);
      });

      form.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        if (target.type === 'checkbox' || target.type === 'radio') {
          syncFitParamsToForm(form);
          form.requestSubmit();
        }
      });
    });

    document.querySelectorAll('[data-sgg-sort-form] select').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      syncFitParamsToForm(select.form);

      select.addEventListener('change', () => {
        syncFitParamsToForm(select.form);
        select.form?.requestSubmit();
      });
    });

    document.querySelectorAll('[data-sgg-preserve-fit]').forEach((link) => {
      preserveFitParamsOnLink(link);
    });
  }

  function initModelRail() {
    const groups = Array.from(document.querySelectorAll('[data-sgg-model-nav-group]'));

    if (!groups.length) return;

    function closeGroup(group) {
      if (!(group instanceof HTMLElement)) return;

      group.classList.remove('is-open');
      const trigger = group.querySelector('[data-sgg-model-trigger]');
      if (trigger instanceof HTMLButtonElement) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    }

    function openGroup(group) {
      if (!(group instanceof HTMLElement)) return;

      groups.forEach((candidate) => {
        if (candidate !== group) {
          closeGroup(candidate);
        }
      });

      group.classList.add('is-open');
      const trigger = group.querySelector('[data-sgg-model-trigger]');
      if (trigger instanceof HTMLButtonElement) {
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    groups.forEach((group) => {
      if (!(group instanceof HTMLElement)) return;

      const trigger = group.querySelector('[data-sgg-model-trigger]');
      if (!(trigger instanceof HTMLButtonElement)) return;

      if (group.classList.contains('is-open')) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      trigger.addEventListener('click', () => {
        const isOpen = group.classList.contains('is-open');
        if (isOpen) {
          closeGroup(group);
        } else {
          openGroup(group);
        }
      });
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      groups.forEach((group) => {
        if (!group.contains(target)) {
          closeGroup(group);
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      groups.forEach((group) => closeGroup(group));
    });
  }

  function initFitChoosers() {
    document.querySelectorAll('[data-sgg-fit-chooser]').forEach((chooser) => {
      if (!(chooser instanceof HTMLElement)) return;

      const brandSelect = chooser.querySelector('[data-sgg-fit-brand]');
      const modelSelect = chooser.querySelector('[data-sgg-fit-model]');

      if (!(brandSelect instanceof HTMLSelectElement) || !(modelSelect instanceof HTMLSelectElement)) {
        return;
      }

      brandSelect.addEventListener('change', () => {
        const brandOption = brandSelect.selectedOptions[0];
        populateChooserModelSelect(modelSelect, brandOption, '');
        updateChooserSubmit(chooser, null);
      });

      modelSelect.addEventListener('change', () => {
        const selection = getChooserSelection(chooser);

        if (!selection) {
          updateChooserSubmit(chooser, null);
          return;
        }
        updateChooserSubmit(chooser, selection);
      });

      chooser.addEventListener('submit', (event) => {
        event.preventDefault();

        const selection = getChooserSelection(chooser);

        if (!selection) return;

        setStoredFitSelection(selection);

        const kitRoot = chooser.closest('[data-sgg-kit-builder]');
        if (kitRoot instanceof HTMLElement) {
          refreshKitBuilderPresets(kitRoot);
          document.getElementById('sgg-kit-presets')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        window.location.href = buildFitCollectionUrl(selection);
      });
    });
  }

  function kitPresetMatches(preset, selection) {
    if (!selection?.brand || !selection.model) return false;

    const brand = preset.dataset.sggKitBrand || '';
    const model = (preset.dataset.sggKitModel || '').trim();

    if (brand !== selection.brand) return false;
    if (!model) return true;

    return model === selection.model;
  }

  function refreshKitBuilderPresets(root) {
    if (!(root instanceof HTMLElement)) return;

    const chooser = root.querySelector('[data-sgg-fit-chooser]');
    const prompt = root.querySelector('[data-sgg-kit-builder-prompt]');
    const empty = root.querySelector('[data-sgg-kit-builder-empty]');
    const emptyActions = root.querySelector('[data-sgg-kit-builder-empty-actions]');
    const callout = root.querySelector('[data-sgg-kit-builder-callout]');
    const presets = root.querySelectorAll('[data-sgg-kit-preset]');

    const selection = chooser instanceof HTMLElement ? getChooserSelection(chooser) : null;

    if (!selection?.brand || !selection?.model) {
      presets.forEach((node) => {
        if (node instanceof HTMLElement) node.hidden = true;
      });
      if (prompt instanceof HTMLElement) prompt.hidden = false;
      if (empty instanceof HTMLElement) empty.hidden = true;
      if (emptyActions instanceof HTMLElement) emptyActions.hidden = true;
      if (callout instanceof HTMLElement) callout.hidden = false;
      return;
    }

    if (prompt instanceof HTMLElement) prompt.hidden = true;

    let visible = 0;
    presets.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const match = kitPresetMatches(node, selection);
      node.hidden = !match;
      if (match) visible += 1;
    });

    if (empty instanceof HTMLElement) {
      empty.hidden = visible > 0;
    }
    if (emptyActions instanceof HTMLElement) {
      emptyActions.hidden = visible > 0;
    }
    if (callout instanceof HTMLElement) {
      callout.hidden = visible > 0;
    }
  }

  function initKitBuilder() {
    document.querySelectorAll('[data-sgg-kit-builder]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;

      const chooser = root.querySelector('[data-sgg-fit-chooser]');
      if (!(chooser instanceof HTMLElement)) return;

      const brandSelect = chooser.querySelector('[data-sgg-fit-brand]');
      const modelSelect = chooser.querySelector('[data-sgg-fit-model]');

      const queueRefresh = () => {
        window.requestAnimationFrame(() => refreshKitBuilderPresets(root));
      };

      if (brandSelect instanceof HTMLSelectElement) {
        brandSelect.addEventListener('change', queueRefresh);
      }
      if (modelSelect instanceof HTMLSelectElement) {
        modelSelect.addEventListener('change', queueRefresh);
      }

      refreshKitBuilderPresets(root);

      root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('[data-sgg-kit-add]');
        if (!(button instanceof HTMLButtonElement) || !root.contains(button)) return;

        const variantId = Number(button.getAttribute('data-variant-id') || '0');
        if (!variantId || button.disabled) return;

        const original = button.textContent || 'Add kit to cart';
        button.disabled = true;
        button.textContent = 'Adding…';

        addToCartWithSections(variantId, 1)
          .then(() => {
            button.textContent = 'Added ✓';
            setTimeout(() => {
              button.textContent = original;
              button.disabled = false;
            }, 2200);
          })
          .catch(() => {
            button.textContent = 'Try again';
            button.disabled = false;
            setTimeout(() => {
              button.textContent = original;
            }, 2200);
          });
      });
    });
  }

  function initCollectionFitFiltering() {
    const params = getFitParams();
    syncFitFilterLinks();

    document.querySelectorAll('[data-sgg-collection-root]').forEach((root) => {
      const cards = Array.from(root.querySelectorAll('[data-sgg-product-card]'));
      const countNode = root.querySelector('[data-sgg-results-count]');
      const activeFilters = root.querySelector('[data-sgg-fit-active-filters]');
      const emptyState = root.querySelector('[data-sgg-empty-state]');
      const pagination = root.querySelector('[data-sgg-pagination]');

      renderFitFilterSummary(activeFilters, params);

      if (!cards.length) {
        updateResultsLabel(countNode, 0, params);
        return;
      }

      let visibleCount = 0;

      cards.forEach((card) => {
        if (!(card instanceof HTMLElement)) return;

        const brands = readCsv(card.dataset.sggBrandKeys);
        const models = readCsv(card.dataset.sggModelKeys);
        const universal = card.dataset.sggUniversal === 'true';

        const speakerLayoutFit = card.dataset.sggFitCheckMode === 'speaker-layout';
        const matchesBrand = !params.brand || brands.includes(params.brand) || speakerLayoutFit;
        const matchesModel =
          !params.model || models.includes(params.model) || (universal && matchesBrand);
        const isVisible = matchesBrand && matchesModel;

        card.hidden = !isVisible;
        card.classList.toggle('is-hidden', !isVisible);

        if (isVisible) {
          visibleCount += 1;
        }
      });

      updateResultsLabel(countNode, visibleCount, params);

      if (emptyState instanceof HTMLElement) {
        const shouldShow = visibleCount === 0 && Boolean(params.brand || params.model);

        if (shouldShow && !emptyState.hasChildNodes()) {
          emptyState.innerHTML = [
            '<h2>No products match that model yet.</h2>',
            '<p>Try a different model, switch accessory type, or use support if you want a manual fit check before buying.</p>',
            '<a class="sgg-filter-pill is-active" data-sgg-clear-fit-link href="#">Clear model filter</a>'
          ].join('');
        }

        const emptyStateClear = emptyState.querySelector('[data-sgg-clear-fit-link]');
        if (emptyStateClear instanceof HTMLAnchorElement) {
          const clearUrl = new URL(window.location.href);
          clearUrl.searchParams.delete('brand');
          clearUrl.searchParams.delete('model');
          emptyStateClear.href = `${clearUrl.pathname}${clearUrl.search}${clearUrl.hash}`;
        }

        emptyState.hidden = !shouldShow;
      }

      if (pagination instanceof HTMLElement) {
        pagination.hidden = Boolean(params.brand || params.model);
      }
    });
  }

  function initHomeBestsellerFilters() {
    document.querySelectorAll('[data-sgg-home-bestsellers-root]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;

      const grid = root.querySelector('[data-sgg-home-bestsellers-grid]');
      if (!(grid instanceof HTMLElement)) return;

      const cards = Array.from(grid.querySelectorAll('[data-sgg-product-card]'));
      const emptyEl = root.querySelector('[data-sgg-home-bestsellers-empty]');
      const countEl = root.querySelector('[data-sgg-home-bestsellers-count]');
      const totalEl = root.querySelector('[data-sgg-home-bestsellers-total]');
      const sepEl = root.querySelector('[data-sgg-home-bestsellers-count-sep]');

      const setActiveGroup = (selector, activeBtn) => {
        root.querySelectorAll(selector).forEach((node) => {
          if (!(node instanceof HTMLButtonElement)) return;
          const on = node === activeBtn;
          node.classList.toggle('is-active', on);
          node.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      };

      const applyFilters = () => {
        const brandBtn = root.querySelector('.sgg-home-filter--brand.is-active');
        const catBtn = root.querySelector('.sgg-home-filter--category.is-active');
        const brandVal = (brandBtn?.getAttribute('data-value') || 'all').trim();
        const catVal = (catBtn?.getAttribute('data-value') || 'all').trim();

        let visible = 0;

        cards.forEach((card) => {
          if (!(card instanceof HTMLElement)) return;

          const brands = readCsv(card.getAttribute('data-sgg-brand-keys'));
          const categories = readCsv(card.getAttribute('data-sgg-category-keys'));
          const universal = card.getAttribute('data-sgg-universal') === 'true';

          const matchesBrand = brandVal === 'all' || universal || brands.includes(brandVal);

          /* Do not let "universal" bypass category — that made every chip a no-op when most SKUs are universal. */
          const matchesCategory =
            catVal === 'all' || (categories.length > 0 && categories.includes(catVal));

          const show = matchesBrand && matchesCategory;
          card.hidden = !show;
          card.classList.toggle('is-hidden', !show);
          if (show) visible += 1;
        });

        if (countEl) countEl.textContent = String(visible);
        if (totalEl) totalEl.textContent = String(cards.length);
        if (sepEl instanceof HTMLElement) {
          sepEl.hidden = cards.length === 0;
        }

        if (emptyEl instanceof HTMLElement) {
          if (visible === 0 && !emptyEl.hasChildNodes()) {
            emptyEl.innerHTML = [
              '<p>No products match those filters.</p>',
              '<p><button type="button" class="sgg-home-bestsellers-reset" data-sgg-home-bestsellers-reset>Reset filters</button> or <a href="/collections/all">browse the full catalog</a>.</p>'
            ].join('');
          }
          emptyEl.hidden = visible > 0;
        }
      };

      root.addEventListener('click', (e) => {
        const t = eventTargetElement(e);
        if (!t) return;

        const filterBtn = t.closest('.sgg-home-filter--brand, .sgg-home-filter--category');
        if (filterBtn instanceof HTMLButtonElement && root.contains(filterBtn)) {
          const sel = filterBtn.classList.contains('sgg-home-filter--brand')
            ? '.sgg-home-filter--brand'
            : '.sgg-home-filter--category';
          setActiveGroup(sel, filterBtn);
          applyFilters();
          return;
        }

        if (t.closest('[data-sgg-home-bestsellers-reset]')) {
          const allBrand = root.querySelector('.sgg-home-filter--brand[data-value="all"]');
          const allCat = root.querySelector('.sgg-home-filter--category[data-value="all"]');
          if (allBrand instanceof HTMLButtonElement) setActiveGroup('.sgg-home-filter--brand', allBrand);
          if (allCat instanceof HTMLButtonElement) setActiveGroup('.sgg-home-filter--category', allCat);
          applyFilters();
        }
      });

      applyFilters();
    });
  }

  function initProductPage() {
    document.querySelectorAll('[data-sgg-product-root]').forEach((root) => {
      const variantsNode = root.querySelector('[data-sgg-variants-json]');
      const form = root.querySelector('[data-sgg-product-form]');
      const variantInput = root.querySelector('[data-sgg-variant-id]');
      const priceNode = root.querySelector('[data-sgg-price]');
      const comparePriceNode = root.querySelector('[data-sgg-compare-price]');
      const salePillNode = root.querySelector('[data-sgg-sale-pill]');
      const saleNoteNode = root.querySelector('[data-sgg-sale-note]');
      const imageSaleBadge = root.querySelector('[data-sgg-image-sale-badge]');
      const imageSaleSave = root.querySelector('[data-sgg-image-sale-save]');
      const imageSalePrice = root.querySelector('[data-sgg-image-sale-price]');
      const imageSaleKicker = root.querySelector('.sgg-product-image-badge-kicker');
      const skuLine = root.querySelector('.sgg-product-itemno');
      const skuNode = root.querySelector('[data-sgg-sku]');
      const submitButton = root.querySelector('[data-sgg-submit]');
      const stickyBar = root.querySelector('[data-sgg-sticky-bar]');
      const stickyPrice = root.querySelector('[data-sgg-sticky-price]');
      const stickyVariant = root.querySelector('[data-sgg-sticky-variant]');
      const purchaseSelection = root.querySelector('[data-sgg-purchase-selection]');
      const stickySubmit = root.querySelector('[data-sgg-sticky-submit]');
      const mainImage = root.querySelector('[data-sgg-main-image]');
      const thumbButtons = root.querySelectorAll('[data-sgg-thumb]');
      const mediaNavButtons = root.querySelectorAll('[data-sgg-media-nav]');
      const optionButtons = root.querySelectorAll('[data-sgg-option-button]');
      const qtyInput = root.querySelector('[data-sgg-qty-input]');
      const reviewButtons = root.querySelectorAll('[data-sgg-open-review-form], .sgg-product-review-entry-button');
      const isVaultChargePage = window.location.pathname.includes('/products/charging-case-for-rokid-ai-glasses');

      if (isVaultChargePage) {
        const titleNode = root.querySelector('.sgg-product-title');
        const brandNode = root.querySelector('.sgg-product-brand');
        const priceRow = root.querySelector('.sgg-product-price-row');
        const heroCopy = {
          subtitle:
            'One case that protects, docks, and recharges Rokid Glasses between uses. Cleaner daily carry, cleaner charging routine.',
          chips: ['Dock-and-charge case', 'Rokid-specific fit', 'USB-C recharge', 'Black or Green', '30-day returns']
        };

        if (titleNode instanceof HTMLElement) {
          let subtitleNode = root.querySelector('.sgg-product-subtitle');
          if (!(subtitleNode instanceof HTMLElement)) {
            subtitleNode = document.createElement('p');
            subtitleNode.className = 'sgg-product-subtitle';
            if (priceRow instanceof HTMLElement) {
              priceRow.insertAdjacentElement('beforebegin', subtitleNode);
            } else {
              titleNode.insertAdjacentElement('afterend', subtitleNode);
            }
          }
          subtitleNode.textContent = heroCopy.subtitle;

          let chipsNode = root.querySelector('.sgg-product-feature-chips');
          if (!(chipsNode instanceof HTMLElement)) {
            chipsNode = document.createElement('div');
            chipsNode.className = 'sgg-product-feature-chips';
            chipsNode.setAttribute('aria-label', 'Product highlights');
            subtitleNode.insertAdjacentElement('afterend', chipsNode);
          }
          chipsNode.innerHTML = '';
          heroCopy.chips.forEach((label) => {
            const chip = document.createElement('span');
            chip.textContent = label;
            chipsNode.appendChild(chip);
          });
        }

        if (brandNode instanceof HTMLElement) {
          const existingBanner = root.querySelector('.sgg-product-attention-banner');
          if (!(existingBanner instanceof HTMLElement)) {
            const banner = document.createElement('div');
            banner.className = 'sgg-product-attention-banner';
            banner.setAttribute('aria-label', 'Product highlight');
            banner.innerHTML =
              '<span class="sgg-product-attention-dot" aria-hidden="true"></span><span>Popular pick</span>';
            brandNode.insertAdjacentElement('beforebegin', banner);
          }
        }

        root.querySelectorAll('.sgg-product-positioning-card').forEach((card) => {
          if (!(card instanceof HTMLElement)) return;
          const kicker = card.querySelector('.sgg-product-positioning-kicker');
          const kickerText = kicker?.textContent?.trim();

          if (kickerText === 'What it does') {
            const paragraph = card.querySelector('p');
            if (paragraph instanceof HTMLElement) {
              paragraph.textContent =
                'SGG VAULT Charge gives Rokid Glasses and Rokid AI Glasses a proper docked home: store them closed, recharge them inside the case, and stop juggling a loose cable between sessions.';
            }
          }

          if (kickerText === 'Works with') {
            const items = card.querySelectorAll('li');
            if (items[0]) {
              items[0].textContent =
                'Made for Rokid Glasses and Rokid AI Glasses with the charging-contact layout shown in the gallery.';
            }
            if (items[1]) {
              items[1].textContent =
                'Best for commute bags, desks, travel kits, and anyone who takes the glasses on and off throughout the day.';
            }
            if (items[2]) {
              items[2].textContent =
                'Choose it when your real bottleneck is messy storage and charging between uses, not extra runtime while you are wearing the glasses.';
            }
          }

          if (kickerText === 'Why this instead') {
            const paragraph = card.querySelector('p');
            if (paragraph instanceof HTMLElement) {
              paragraph.textContent =
                'Unlike a plain carry case, VAULT Charge protects and powers at the same time. Unlike leaving the glasses on a desk cable, it keeps the full setup packed, charged, and ready to grab.';
            }
          }
        });
      }

      if (!(variantsNode instanceof HTMLScriptElement) || !(form instanceof HTMLFormElement)) return;

      let variants = [];

      try {
        variants = JSON.parse(variantsNode.textContent || '[]');
      } catch (error) {
        variants = [];
      }

      const selected = [];
      root.querySelectorAll('[data-sgg-selected-label]').forEach((label) => {
        const position = Number(label.getAttribute('data-sgg-selected-label')) - 1;
        selected[position] = label.textContent?.trim() || '';
      });

      function setActiveThumb(mediaId) {
        thumbButtons.forEach((button) => {
          const isActive = button.getAttribute('data-media-id') === String(mediaId);
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      function updateMainImage(src, alt, mediaId, srcset, sizes) {
        if (!(mainImage instanceof HTMLImageElement) || !src) return;

        if (srcset) {
          mainImage.srcset = srcset;
        } else {
          mainImage.removeAttribute('srcset');
        }

        if (sizes) {
          mainImage.sizes = sizes;
        } else {
          mainImage.removeAttribute('sizes');
        }

        mainImage.src = src;
        mainImage.alt = alt || mainImage.alt;
        mainImage.dataset.sggMainMediaId = mediaId ? String(mediaId) : '';

        if (mediaId) {
          setActiveThumb(mediaId);
        }
      }

      function getActiveThumbIndex() {
        const currentMediaId =
          mainImage instanceof HTMLImageElement ? mainImage.dataset.sggMainMediaId || '' : '';
        const buttons = Array.from(thumbButtons);

        if (currentMediaId) {
          const matchIndex = buttons.findIndex(
            (button) => button.getAttribute('data-media-id') === String(currentMediaId)
          );
          if (matchIndex >= 0) return matchIndex;
        }

        return buttons.findIndex((button) => button.classList.contains('is-active'));
      }

      function cycleMainImage(step) {
        const buttons = Array.from(thumbButtons).filter((button) => button instanceof HTMLButtonElement);
        if (buttons.length <= 1) return;

        const currentIndex = getActiveThumbIndex();
        const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
        const nextIndex = (safeCurrentIndex + step + buttons.length) % buttons.length;
        const nextButton = buttons[nextIndex];

        if (!(nextButton instanceof HTMLButtonElement)) return;

        updateMainImage(
          nextButton.getAttribute('data-media-src'),
          nextButton.getAttribute('data-media-alt'),
          nextButton.getAttribute('data-media-id'),
          nextButton.getAttribute('data-media-srcset'),
          nextButton.getAttribute('data-media-sizes')
        );
      }

      function updateVariant(variant) {
        if (!variant) {
          if (submitButton instanceof HTMLButtonElement) {
            submitButton.disabled = true;
            submitButton.textContent = 'Unavailable';
          }
          return;
        }

        if (variantInput instanceof HTMLInputElement) {
          variantInput.value = String(variant.id);
        }

        if (priceNode) {
          priceNode.textContent = window.money(variant.price);
        }

        if (comparePriceNode instanceof HTMLElement) {
          const compareAt = Number(variant.compare_at_price || 0);
          const price = Number(variant.price || 0);
          if (compareAt > price) {
            comparePriceNode.hidden = false;
            comparePriceNode.textContent = window.money(compareAt);
          } else {
            comparePriceNode.hidden = true;
            comparePriceNode.textContent = '';
          }
        }

        if (salePillNode instanceof HTMLElement) {
          const compareAt = Number(variant.compare_at_price || 0);
          const price = Number(variant.price || 0);
          if (compareAt > price) {
            salePillNode.hidden = false;
            salePillNode.textContent = `Save ${window.money(compareAt - price)}`;
          } else {
            salePillNode.hidden = true;
            salePillNode.textContent = '';
          }
        }

        if (saleNoteNode instanceof HTMLElement) {
          const compareAt = Number(variant.compare_at_price || 0);
          const price = Number(variant.price || 0);
          saleNoteNode.textContent =
            compareAt > price ? 'On sale · Free U.S. shipping included' : 'Free U.S. standard shipping';
        }

        if (imageSaleBadge instanceof HTMLElement) {
          const compareAt = Number(variant.compare_at_price || 0);
          const price = Number(variant.price || 0);
          if (imageSaleKicker instanceof HTMLElement) {
            imageSaleKicker.textContent = compareAt > price ? 'On sale' : '';
          }
          if (compareAt > price) {
            imageSaleBadge.hidden = false;
            if (imageSaleSave instanceof HTMLElement) {
              imageSaleSave.textContent = `Save ${window.money(compareAt - price)}`;
            }
            if (imageSalePrice instanceof HTMLElement) {
              imageSalePrice.textContent = `Now ${window.money(price)}`;
            }
          } else {
            imageSaleBadge.hidden = true;
            if (imageSaleSave instanceof HTMLElement) imageSaleSave.textContent = '';
            if (imageSalePrice instanceof HTMLElement) imageSalePrice.textContent = '';
          }
        }

        if (skuNode) {
          const sku = variant.sku || '';
          skuNode.textContent = sku;
          if (skuLine instanceof HTMLElement) {
            skuLine.hidden = !sku;
          }
        }

        if (stickyPrice) {
          stickyPrice.textContent = window.money(variant.price);
        }

        if (stickyVariant) {
          stickyVariant.textContent = variant.title === 'Default Title' ? '' : variant.title || '';
        }

        if (purchaseSelection instanceof HTMLElement && variant.title !== 'Default Title') {
          purchaseSelection.textContent = `Selected: ${variant.title}.`;
        }

        if (submitButton instanceof HTMLButtonElement) {
          submitButton.disabled = !variant.available;
          submitButton.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }

        if (stickySubmit instanceof HTMLButtonElement) {
          stickySubmit.disabled = !variant.available;
          stickySubmit.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }

        if (variant.featured_media_id) {
          const activeThumb = root.querySelector(`[data-sgg-thumb][data-media-id="${variant.featured_media_id}"]`);
          if (activeThumb instanceof HTMLButtonElement) {
            updateMainImage(
              activeThumb.getAttribute('data-media-src'),
              activeThumb.getAttribute('data-media-alt'),
              variant.featured_media_id,
              activeThumb.getAttribute('data-media-srcset'),
              activeThumb.getAttribute('data-media-sizes')
            );
          }
        }
      }

      function getCurrentVariant() {
        if (selected.length === 0) return variants[0];
        return variants.find((variant) =>
          variant.options.every((value, index) => !value || value === selected[index])
        );
      }

      function openReviewForm(attempt = 0) {
        const reviewWidget =
          root.querySelector('#judgeme_product_reviews') || document.getElementById('judgeme_product_reviews');
        const nativeButton = reviewWidget?.querySelector('.jdgm-write-rev-link');
        const fallbackButton = root.querySelector('[data-sgg-open-review-form], .sgg-product-review-entry-button');
        const fallbackHref =
          fallbackButton instanceof HTMLAnchorElement ? fallbackButton.href : '';

        if (nativeButton instanceof HTMLElement) {
          window.sggEnsureJudgeMeModalLayout();
          window.sggBindJudgeMeModalProgressFix();

          if (reviewWidget instanceof HTMLElement) {
            reviewWidget.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }

          window.setTimeout(() => {
            nativeButton.style.display = 'block';
            nativeButton.style.visibility = 'visible';
            nativeButton.dispatchEvent(
              new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
            );
            nativeButton.click();
          }, 120);
          window.setTimeout(() => {
            window.sggEnsureJudgeMeCloseButton();
          }, 280);
          window.setTimeout(() => {
            window.sggEnsureJudgeMeCloseButton();
          }, 900);
          return;
        }

        if (reviewWidget instanceof HTMLElement) {
          reviewWidget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        if (attempt >= 20) {
          if (fallbackHref) {
            window.location.assign(fallbackHref);
          }
          return;
        }

        window.setTimeout(() => {
          openReviewForm(attempt + 1);
        }, 250);
      }

      reviewButtons.forEach((button) => {
        if (!(button instanceof HTMLAnchorElement)) return;

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('write-review', '1');
        currentUrl.hash = 'judgeme_product_reviews';
        button.href = currentUrl.toString();
      });

      optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const position = Number(button.getAttribute('data-position')) - 1;
          const value = button.getAttribute('data-value') || '';

          selected[position] = value;

          optionButtons.forEach((candidate) => {
            const candidatePosition = Number(candidate.getAttribute('data-position')) - 1;
            if (candidatePosition === position) {
              candidate.classList.toggle('is-active', candidate === button);
              candidate.setAttribute('aria-pressed', candidate === button ? 'true' : 'false');
            }
          });

          const label = root.querySelector(`[data-sgg-selected-label="${position + 1}"]`);
          if (label) {
            label.textContent = value;
          }

          updateVariant(getCurrentVariant());
        });
      });

      thumbButtons.forEach((button) => {
        button.addEventListener('click', () => {
          updateMainImage(
            button.getAttribute('data-media-src'),
            button.getAttribute('data-media-alt'),
            button.getAttribute('data-media-id'),
            button.getAttribute('data-media-srcset'),
            button.getAttribute('data-media-sizes')
          );
        });
      });

      mediaNavButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.getAttribute('data-sgg-media-nav')) || 0;
          if (!step) return;
          cycleMainImage(step);
        });
      });

      if (mainImage instanceof HTMLImageElement && thumbButtons.length > 1) {
        mainImage.addEventListener('click', () => {
          cycleMainImage(1);
        });

        mainImage.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          cycleMainImage(1);
        });
      }

      reviewButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          openReviewForm();
        });
      });

      root.querySelectorAll('[data-sgg-qty-change]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!(qtyInput instanceof HTMLInputElement)) return;

          const step = Number(button.getAttribute('data-sgg-qty-change')) || 0;
          const currentValue = Number(qtyInput.value) || 1;
          qtyInput.value = String(Math.max(1, currentValue + step));
        });
      });

      stickySubmit?.addEventListener('click', () => {
        form.requestSubmit();
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();

        if (!(submitButton instanceof HTMLButtonElement)) return;

        const variantId = Number(variantInput instanceof HTMLInputElement ? variantInput.value : '0');
        const qty = qtyInput instanceof HTMLInputElement ? Math.max(1, Number(qtyInput.value) || 1) : 1;
        if (!variantId) return;

        const originalText = submitButton.textContent || 'Add to Cart';
        submitButton.disabled = true;
        submitButton.textContent = 'Adding…';
        submitButton.classList.add('is-loading');

        const stickyBtn = stickySubmit instanceof HTMLButtonElement ? stickySubmit : null;
        if (stickyBtn) { stickyBtn.disabled = true; stickyBtn.textContent = 'Adding…'; }

        addToCartWithSections(variantId, qty)
          .then(() => {
            submitButton.textContent = 'Added to Cart ✓';
            submitButton.classList.remove('is-loading');
            submitButton.classList.add('is-added');
            if (stickyBtn) { stickyBtn.disabled = false; stickyBtn.textContent = 'Added ✓'; stickyBtn.classList.add('is-added'); }
            setTimeout(() => {
              submitButton.textContent = originalText;
              submitButton.classList.remove('is-added');
              submitButton.disabled = false;
              if (stickyBtn) { stickyBtn.textContent = 'Add to Cart'; stickyBtn.classList.remove('is-added'); }
            }, 2500);
          })
          .catch(() => {
            submitButton.textContent = 'Try again';
            submitButton.classList.remove('is-loading');
            submitButton.classList.add('is-error');
            submitButton.disabled = false;
            if (stickyBtn) { stickyBtn.disabled = false; stickyBtn.textContent = 'Try again'; }
            setTimeout(() => {
              submitButton.textContent = originalText;
              submitButton.classList.remove('is-error');
              if (stickyBtn) stickyBtn.textContent = 'Add to Cart';
            }, 2500);
          });
      });

      if (stickyBar && submitButton instanceof HTMLButtonElement) {
        const toggleSticky = () => {
          const rect = submitButton.getBoundingClientRect();
          const shouldShow = rect.bottom < 0;
          stickyBar.hidden = !shouldShow;
        };

        let stickyTicking = false;
        const queueStickyToggle = () => {
          if (stickyTicking) return;
          stickyTicking = true;

          window.requestAnimationFrame(() => {
            stickyTicking = false;
            toggleSticky();
          });
        };

        toggleSticky();
        document.addEventListener('scroll', queueStickyToggle, { passive: true });
        window.addEventListener('resize', queueStickyToggle);
      }

      updateVariant(getCurrentVariant());

      if (window.location.hash === '#judgeme_product_reviews' || window.location.search.includes('write-review=1')) {
        window.setTimeout(() => {
          openReviewForm();
        }, 800);
      }
    });
  }

  function initFitSelection() {
    let pageSelection = null;

    document.querySelectorAll('[data-sgg-collection-root]').forEach((root) => {
      const contextSelection = getCollectionContextSelection(root);

      if (contextSelection) {
        pageSelection = contextSelection;
      }
    });

    const storedSelection = getStoredFitSelection();
    refreshFitUi(pageSelection || storedSelection);
  }

  /** Section IDs for cart-items-component (header drawer + cart page, etc.) — required for cart HTML refresh. */
  function getCartItemsSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }

  /**
   * Horizon / cart-items listen for `cart:update` and morph drawer HTML from `detail.data.sections`.
   * Plain /cart/add.js without sections leaves the drawer stuck on the empty first paint.
   */
  function dispatchThemeCartUpdate(cart, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'sgg-theme',
          data: {
            itemCount: cart.item_count,
            source: 'sgg-theme-ajax-add',
            sections: sections || {},
          },
        },
      })
    );
  }

  function addToCartWithSections(variantId, quantity) {
    const sectionIds = getCartItemsSectionIds();
    const payload = {
      items: [{ id: variantId, quantity }],
    };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || (data.status && Number(data.status) >= 400)) {
          const msg = data.description || data.message || 'Could not add to cart';
          throw new Error(msg);
        }
        return data;
      })
      .then((addData) =>
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => ({ addData, cart }))
      )
      .then(({ addData, cart }) => {
        dispatchThemeCartUpdate(cart, addData.sections);
        return cart;
      });
  }

  function initAjaxCardButtons() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.classList.contains('sgg-card-form')) return;

      event.preventDefault();

      const button = form.querySelector('.sgg-card-button');
      if (!(button instanceof HTMLButtonElement)) return;

      const variantIdInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector('input[name="quantity"]');

      if (!(variantIdInput instanceof HTMLInputElement)) return;

      const variantId = Number(variantIdInput.value);
      const quantity = Number(quantityInput?.value || '1') || 1;

      if (!variantId) return;

      const originalText = button.textContent || 'Add';
      button.disabled = true;
      button.textContent = '…';
      button.classList.add('is-loading');

      addToCartWithSections(variantId, quantity)
        .then(() => {
          button.textContent = 'Added';
          button.classList.remove('is-loading');
          button.classList.add('is-added');

          setTimeout(() => {
            button.textContent = originalText;
            button.classList.remove('is-added');
            button.disabled = false;
          }, 2200);
        })
        .catch(() => {
          button.textContent = 'Try again';
          button.classList.remove('is-loading');
          button.classList.add('is-error');
          button.disabled = false;

          setTimeout(() => {
            button.textContent = originalText;
            button.classList.remove('is-error');
          }, 2200);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initCollectionDisclosures();
    initCollectionControls();
    initModelRail();
    initFitChoosers();
    bindFitSelectionTriggers();
    initHomeBestsellerFilters();
    initCollectionFitFiltering();
    initProductPage();
    initFitSelection();
    initAjaxCardButtons();
    initKitBuilder();
  });
})();

// Keep live chat available without making its large widget bundle compete with
// the homepage hero and product content during the critical loading window.
(function deferTidioChat() {
  var loaded = false;
  var timer;
  var interactionEvents = ['pointerdown', 'keydown', 'touchstart'];

  function load() {
    if (loaded || document.querySelector('script[src*="code.tidio.co"]')) return;
    loaded = true;
    window.clearTimeout(timer);
    interactionEvents.forEach(function (eventName) {
      window.removeEventListener(eventName, load);
    });

    var script = document.createElement('script');
    script.src = 'https://code.tidio.co/rlqq8mamk1bbdhxauboledk1jv3skrgi.js?extensionVersion=1.4.1';
    script.async = true;
    document.head.appendChild(script);
  }

  interactionEvents.forEach(function (eventName) {
    window.addEventListener(eventName, load, { once: true, passive: true });
  });

  window.addEventListener('load', function () {
    timer = window.setTimeout(load, 30000);
  }, { once: true });
})();

// Keep mobile chat compact across the storefront without disabling chat.
// Tidio's supported style API reaches its own widget; page CSS cannot.
(function compactMobileChatLabel() {
  var compactProduct = document.querySelector('main[data-template^="product"]');
  var applied = false;

  function applyCompactLabel() {
    if (applied || !window.tidioChatApi || typeof window.tidioChatApi.adjustStyles !== 'function') return;
    // The floating welcome preview can cover purchase controls on narrow screens.
    // Keep the launcher, unread badge, and user-opened conversation available.
    var chatStyle = '@media only screen and (max-width: 749px) { [data-testid="widgetLabel"], [data-testid="flyMessage"] { display: none !important; }';
    if (compactProduct) chatStyle += ' #tidio { bottom: calc(100px + env(safe-area-inset-bottom)) !important; }';
    chatStyle += ' }';
    window.tidioChatApi.adjustStyles(chatStyle);
    applied = true;
  }

  if (window.tidioChatApi) {
    window.tidioChatApi.on('ready', applyCompactLabel);
  } else {
    document.addEventListener('tidioChat-ready', applyCompactLabel, { once: true });
  }
})();
